import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useProducts } from './useProducts';
import { buildCategoryGroupCards, thumbForGroup } from './catalogGroupUtils';
import { firstProductImage } from './catalog/catalogMedia';
import CatalogImage from './catalog/CatalogImage';
import { formatDisplayTitle } from './formatProductTitle';
import { categoryHeroBlurb, categoryHeroTagline } from './categoryHeroText';

/**
 * Landing page for any category slug (e.g. /shop/gifts). Rakhi & Lumba use dedicated pages via route order.
 */
const DynamicCategoryHub = () => {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const { products, categories, loading } = useProducts();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categorySlug]);

  const meta = categories.find((c) => c.slug === categorySlug);
  const list = useMemo(() => products[categorySlug] || [], [products, categorySlug]);

  const groupCards = useMemo(() => buildCategoryGroupCards(meta, list), [meta, list]);

  const hubTagline = categoryHeroTagline(meta, '');
  const defaultHeroBlurb =
    list.length > 0
      ? `${list.length} handcrafted design${list.length === 1 ? '' : 's'} — explore styles below.`
      : 'New pieces are added often — please check back soon.';

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <p className="text-muted">Loading collection…</p>
      </div>
    );
  }

  const title = formatDisplayTitle(meta?.name || categorySlug.replace(/-/g, ' '));

  if (!list.length) {
    return (
      <div className="collections-page">
        <div className="container store-page__container">
          <section className="collections-hero">
            <div className="collections-hero-panel text-center">
              <h1 className="collections-page-title">{title}</h1>
              {hubTagline ? <p className="collections-hero-tagline">{hubTagline}</p> : null}
              <p className="collections-page-subtitle handwritten">
                {categoryHeroBlurb(meta, defaultHeroBlurb)}
              </p>
            </div>
          </section>
          <section className="collections-grid-section">
            <div className="text-center py-5">
              <p className="text-muted mb-3">No products in this category yet.</p>
              <Link to="/shop" className="btn btn-outline-primary">
                Back to shop
              </Link>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="collections-page">
      <div className="container store-page__container">
        <section className="collections-hero">
          <div className="collections-hero-panel text-center">
            <h1 className="collections-page-title">{title}</h1>
            {hubTagline ? <p className="collections-hero-tagline">{hubTagline}</p> : null}
            <p className="collections-page-subtitle handwritten">
              {categoryHeroBlurb(meta, defaultHeroBlurb)}
            </p>
          </div>
        </section>

        <section className="collections-grid-section">
          <div className="row g-4">
            {groupCards.length === 0 ? (
              <div className="col-12 text-center py-4">
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  onClick={() => navigate(`/shop/${categorySlug}/all`)}
                >
                  View all products
                </button>
              </div>
            ) : (
              groupCards.map((card) => {
                const thumb = thumbForGroup(list, card.key) || firstProductImage(list);
                return (
                  <div className="col-6 col-md-4 col-lg-3" key={card.key}>
                    <div
                      className="collection-type-card"
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        navigate(`/shop/${categorySlug}/${encodeURIComponent(card.key)}`)
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          navigate(`/shop/${categorySlug}/${encodeURIComponent(card.key)}`);
                        }
                      }}
                    >
                      <div className="type-image-wrapper">
                        <CatalogImage
                          src={thumb}
                          alt={card.label}
                          className="type-image"
                          sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
                        />
                        <div className="type-overlay">
                          <span className="view-collection">View</span>
                        </div>
                      </div>
                      <div className="type-content">
                        <h3 className="type-title">{card.label}</h3>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default DynamicCategoryHub;
